import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { SharpServiceNames } from './client-manager.interface';
import { InitializableInterface } from './initializable.interface';
import { CompositeModel } from '../models/complex/composite.model';
import { UserModel } from '../models/complex/user.model';
import { User } from '../types/user';

/**
 * Public contract of the user-master service: the selected sales-org/plant user chosen
 * during initialization plus the getters and URL/model helpers that expose it. Extends
 * {@link InitializableInterface} (`isInitialized` + `init()`).
 *
 * Implemented by both the legacy `UserMasterService` and the NgRx-backed
 * `UserMasterNgrxService`; consumers depend on this interface via
 * {@link UserMasterInstanceToken} rather than on a concrete class.
 */
export interface UserMasterInterface extends InitializableInterface {
    /** Default #-service text used as a fallback before/without a client-type match. */
    defaultSharpServiceText: SharpServiceNames;

    /** Load the user master data and resolve the selected sales-org/plant user. */
    init(): Observable<void>;

    /**
     * The selected user as a model instance.
     *
     * @throws if the service is not initialized or no user has been selected.
     */
    getSelectedUserModel(): UserModel;

    /** The selected user as a plain, read-only value object. */
    getSelectedUser(): User;

    /** The selected user's role (empty string when unknown). */
    getUserRole(): Observable<string>;

    /** Whether the current user is an external (PACE Online) user rather than internal. */
    isPaceOnlineUser(): boolean;

    /** The #-service text for the active client/user combination. */
    getSharpServiceText(): SharpServiceNames;

    /** Set the selected user's SalesOrg and Plant onto the given composite model. */
    setSalesOrgAndPlantForModel(model: CompositeModel): void;

    /** Append the selected user's identifying filters to the given OData query. */
    setSalesOrgAndPlantForUrl(query: string): string;
}

/**
 * DI token consumers use to resolve a {@link UserMasterInterface} implementation. The
 * concrete provider (legacy vs. NgRx) is chosen per app module via the `enableNgRx`
 * environment flag.
 */
export const UserMasterInstanceToken = new InjectionToken<UserMasterInterface>('UserMasterInterface');
